import { jwtDecode } from 'jwt-decode';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

export interface DecodedJwt {
  sub: string;
  email?: string;
  name?: string;
  tenant_id?: string;
  roles?: string[];
  ['_couchdb.roles']?: string[];
  // "assigned": a technician limited to their calendar customers (client #92).
  sync_scope?: string;
  // Tenants this account was moved away from (set by an operator); the
  // legacy carry-over may bring its own unsent edits tagged with them.
  prev_tenants?: string[];
  exp: number;
  iat?: number;
}

const REFRESH_KEY = 'ft.refreshToken';

type Listener = () => void;

interface AuthState {
  jwt: string | null;
  decoded: DecodedJwt | null;
}

const state: AuthState = { jwt: null, decoded: null };
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach(l => {
    try {
      l();
    } catch {
      // ignore listener errors
    }
  });
}

function safeDecode(jwt: string | null): DecodedJwt | null {
  if (!jwt) return null;
  try {
    const d = jwtDecode<DecodedJwt>(jwt);
    // The API mints CouchDB-shaped claims with `_couchdb.roles`; mirror it onto
    // `roles` so consumers don't need to know about the alternate key.
    if (!d.roles && d['_couchdb.roles']) {
      d.roles = d['_couchdb.roles'];
    }
    return d;
  } catch {
    return null;
  }
}

export function setJwt(jwt: string | null): void {
  state.jwt = jwt;
  state.decoded = safeDecode(jwt);
  emit();
}

export function getJwt(): string | null {
  return state.jwt;
}

export function getDecoded(): DecodedJwt | null {
  return state.decoded;
}

export function onChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isExpired(claims: DecodedJwt | null): boolean {
  if (!claims?.exp) return true;
  // jwt exp is seconds since epoch
  return claims.exp * 1000 <= Date.now();
}

export async function getRefreshToken(): Promise<string | null> {
  try {
    return ((await idbGet(REFRESH_KEY)) as string | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setRefreshToken(token: string | null): Promise<void> {
  if (token == null) {
    await idbDel(REFRESH_KEY);
  } else {
    await idbSet(REFRESH_KEY, token);
  }
}

export async function clearAll(): Promise<void> {
  setJwt(null);
  await setRefreshToken(null);
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

async function rawJson(path: string, body: unknown): Promise<Response> {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';
  return fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function login(
  email: string,
  password: string
): Promise<DecodedJwt> {
  const res = await rawJson('/auth/login', { email, password });
  if (!res.ok) {
    const err = new Error(
      res.status === 401 ? 'Invalid credentials' : 'Login failed'
    );
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const data = (await res.json()) as LoginResponse;
  await setRefreshToken(data.refreshToken);
  setJwt(data.accessToken);
  const decoded = getDecoded();
  if (!decoded) throw new Error('Invalid token returned');
  return decoded;
}

let refreshInFlight: Promise<string> | null = null;

export async function refresh(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    let rt = await getRefreshToken();
    if (!rt) throw new Error('No refresh token');
    let res = await rawJson('/auth/refresh', { refreshToken: rt });
    if (!res.ok) {
      // Another tab of the app may have exchanged the same token a moment ago
      // and stored its successor: use that before giving up.
      const stored = await getRefreshToken();
      if (stored && stored !== rt) {
        rt = stored;
        res = await rawJson('/auth/refresh', { refreshToken: rt });
      }
    }
    if (!res.ok) {
      // Only a rejection of the token ends the session. A server that is
      // restarting (deploy) or failing answers 5xx; wiping the stored token
      // then signed users out on every deploy.
      if (res.status === 400 || res.status === 401) await clearAll();
      throw new Error('Refresh failed');
    }
    const data = (await res.json()) as LoginResponse;
    if (data.refreshToken) await setRefreshToken(data.refreshToken);
    setJwt(data.accessToken);
    return data.accessToken;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function logout(): Promise<void> {
  try {
    const rt = await getRefreshToken();
    if (rt) await rawJson('/auth/logout', { refreshToken: rt });
  } catch {
    // best effort
  } finally {
    // Destroy this login's local replica BEFORE clearing tokens (its name is
    // derived from them). On a shared device the next sign-in must not find
    // the previous user's cached customer data on disk. Awaited so a fast
    // re-login cannot reopen it. Dynamic import breaks the static cycle
    // (couch.ts imports auth.ts).
    try {
      const { destroyLocal } = await import('@/lib/couch');
      await destroyLocal();
    } catch {
      // best effort — token clear below still runs
    }
    await clearAll();
  }
}

// Retries while a session is stored but the server cannot be reached or is
// restarting — the app reloads itself right after a deploy, exactly when the
// API may still be coming up.
const BOOTSTRAP_RETRY_MS = [1500, 3000];

export async function bootstrap(): Promise<DecodedJwt | null> {
  // attempt silent refresh from persisted refresh token
  for (let attempt = 0; ; attempt++) {
    try {
      await refresh();
      return getDecoded();
    } catch {
      // Rejected (tokens cleared) or never signed in: nothing to retry.
      if (!(await getRefreshToken())) return null;
      if (attempt >= BOOTSTRAP_RETRY_MS.length) return null;
      await new Promise(r => setTimeout(r, BOOTSTRAP_RETRY_MS[attempt]));
    }
  }
}
